/**
 * Ikaros cart add-on: the Headwear Case.
 *
 * WHAT IT DOES
 * When the cart holds a cap and no case, it offers the Ikaros Headwear Case
 * in the cart drawer and on the cart page. One tap adds it. Once it is in
 * the cart the offer disappears, so it never nags.
 *
 * WHY IT READS THE PRODUCT AT RUNTIME
 * Title, price, image and variant id come from /products/<handle>.js rather
 * than being written into this file. A price change in admin then needs no
 * code change, and the card can never show a price the cart will not honour
 * - which is the usual way an upsell loses trust.
 *
 * WHY IT WATCHES FETCH RATHER THAN AN EVENT
 * Horizon re-renders the cart by morphing section HTML, not by reloading,
 * so the injected card is wiped on every cart change and there is no single
 * event to hang re-injection on. Watching cart requests catches every path:
 * drawer, cart page, quick add. The same approach as the embroidery guard,
 * for the same reason.
 *
 * RELATION TO THE FREE CASE BUNDLE
 * A separate automatic discount gives the case free at 20 KD of caps. That
 * lives in Shopify discounts, not here. This card only adds the product;
 * if the cart already qualifies, Shopify zeroes the price by itself and the
 * card's note says so. The two never disagree because neither sets a price.
 */

(() => {
  const HANDLE = 'ikaros-headwear-case';
  const CAP_TYPE = 'Cap';
  const FREE_AT = 2000; // minor units - keep in step with the bundle discount

  let product = null;
  let busy = false;
  let dismissed = false;

  try {
    dismissed = sessionStorage.getItem('ikaros-case-dismissed') === '1';
  } catch (e) {
    /* private mode - the card simply keeps offering */
  }

  const money = (minor) => (minor / 100).toFixed(3) + ' KD';

  async function loadProduct() {
    if (product !== null) return product;
    try {
      const res = await fetch('/products/' + HANDLE + '.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('not found');
      const p = await res.json();
      product = {
        title: p.title,
        price: p.price,
        available: p.available,
        variantId: p.variants && p.variants[0] && p.variants[0].id,
        image: p.featured_image || (p.images && p.images[0]) || '',
        url: '/products/' + HANDLE,
      };
    } catch (e) {
      product = false; // remembered, so we do not retry on every cart change
      console.warn('[ikaros-case] product unavailable', e);
    }
    return product;
  }

  function capSpend(cart) {
    return (cart.items || [])
      .filter((i) => (i.product_type || '') === CAP_TYPE)
      .reduce((sum, i) => sum + i.original_line_price, 0);
  }

  function hasCase(cart) {
    return (cart.items || []).some((i) => i.handle === HANDLE);
  }

  function build(p, spend) {
    const qualifies = spend >= FREE_AT;
    const short = Math.max(0, FREE_AT - spend);

    const card = document.createElement('div');
    card.className = 'ikcase';
    card.dataset.ikcase = 'true';

    const img = p.image
      ? '<img class="ikcase__img" src="' + p.image + '&width=160" alt="" loading="lazy" width="64" height="64">'
      : '<span class="ikcase__img ikcase__img--blank"></span>';

    const note = qualifies
      ? '<span class="ikcase__free">Free with your caps</span>'
      : '<span class="ikcase__note">Add ' + money(short) + ' more in caps to get it free</span>';

    const price = qualifies
      ? '<s>' + money(p.price) + '</s> <b>Free</b>'
      : '<b>' + money(p.price) + '</b>';

    card.innerHTML =
      '<div class="ikcase__media">' + img + '</div>' +
      '<div class="ikcase__body">' +
        '<p class="ikcase__eyebrow">Keeps its shape in a bag</p>' +
        '<p class="ikcase__title"><a href="' + p.url + '">' + p.title + '</a></p>' +
        '<p class="ikcase__price">' + price + ' ' + note + '</p>' +
      '</div>' +
      '<button type="button" class="ikcase__add">Add</button>' +
      '<button type="button" class="ikcase__close" aria-label="Not interested">&times;</button>';

    card.querySelector('.ikcase__add').addEventListener('click', () => add(p, card));
    card.querySelector('.ikcase__close').addEventListener('click', () => {
      dismissed = true;
      try { sessionStorage.setItem('ikaros-case-dismissed', '1'); } catch (e) { /* ignore */ }
      card.remove();
    });

    return card;
  }

  async function add(p, card) {
    if (busy) return;
    busy = true;
    const btn = card.querySelector('.ikcase__add');
    btn.disabled = true;
    btn.textContent = 'Adding';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1 }] }),
      });
      if (!res.ok) throw new Error(await res.text());
      // Let Horizon redraw the cart from the server rather than patching the
      // DOM here; the totals and any bundle discount have to come from it.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      console.error('[ikaros-case]', e);
      btn.disabled = false;
      btn.textContent = 'Add';
      const err = document.createElement('span');
      err.className = 'ikcase__err';
      err.textContent = 'Could not add it. Please try again.';
      card.appendChild(err);
    } finally {
      busy = false;
    }
  }

  function hosts() {
    // Drawer first, cart page second. Both may exist on the same page.
    return [
      document.querySelector('.cart-drawer__inner'),
      document.querySelector('.cart-items__wrapper'),
    ].filter(Boolean);
  }

  async function render() {
    if (dismissed) return;
    let cart;
    try {
      cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    } catch (e) {
      return;
    }

    const spend = capSpend(cart);
    const show = spend > 0 && !hasCase(cart);

    document.querySelectorAll('[data-ikcase]').forEach((n) => n.remove());
    if (!show) return;

    const p = await loadProduct();
    if (!p || !p.available || !p.variantId) return;

    hosts().forEach((host) => {
      if (host.querySelector('[data-ikcase]')) return;
      host.appendChild(build(p, spend));
    });
  }

  function start() {
    render();

    const origFetch = window.fetch;
    window.fetch = function (...args) {
      const url = String((args[0] && args[0].url) || args[0] || '');
      const p = origFetch.apply(this, args);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(() => setTimeout(render, 500)).catch(() => {});
      }
      return p;
    };

    ['cart:updated', 'cart:refresh'].forEach((evt) =>
      document.addEventListener(evt, () => setTimeout(render, 500))
    );
    document.addEventListener('click', (e) => {
      if (e.target.closest('[href="/cart"], cart-icon, .header-actions__cart-icon')) {
        setTimeout(render, 700);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
